/* eslint-disable max-lines */
import { Close, Delete, Edit, RestartAlt, Save } from '@mui/icons-material'
import { Box, Button, Chip, Divider, Grid, IconButton, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ILocalitySolidityAndCircumference } from 'src/generated-types/locality-solidity-circumference'
import { Flex } from 'src/styles/flexComponent'
import { generateUUID } from 'src/utils/function'
import { z } from 'zod'

import { AppForm, AppFormProps, FormCheckbox, FormTextField } from '../../../components/FormCore'

import { FormSubmitButton } from 'src/components/FormCore'
import { useGetCustomerUsers } from 'src/api/customer'
import { useZodForm } from 'src/hooks/zod-form'
import BasicDatePicker from 'src/components/DateTimePicker'
import { IFile } from 'src/generated-types'
import { GridActionButton } from 'src/components/common/GridActionButton'

export interface LocalityFormProps extends AppFormProps {
  file?: IFile
  title?: string
  isLoading?: boolean
  customerId?: number
  pop: () => void
  onStepback?: (e) => void
}

export const LocalityForm: React.FC<LocalityFormProps> = ({
  onSubmit,
  initialValues,
  file,
  customerId,
  pop,
  onStepback,
}) => {
  const { t: translate } = useTranslation(['locality', 'common'])
  const isUpdateForm = initialValues?.id
  const properties = ['solidity', 'circumference']
  const [solidityAndCircumferences, setsolidityAndCircumferences] = useState<Array<ILocalitySolidityAndCircumference>>(
    [],
  )
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  const schema = z.object({
    name: z.string().nonempty(translate('nameIsRequired', { ns: 'locality' })),
    locationNr: z.string().nonempty(translate('locationNrIsRequired', { ns: 'locality' })),
    latitude: z
      .string()
      .or(z.number())
      .or(z.null())
      .transform(value => (typeof value === 'string' ? parseFloat(value) : value))
      .optional(),
    longitude: z
      .string()
      .or(z.number())
      .or(z.null())
      .transform(value => (typeof value === 'string' ? parseFloat(value) : value))
      .optional(),
    notificationActive: z.boolean().default(false),
    active: z.boolean().default(true),
    fileId: z.any().optional(),
    logoId: z.any(),
    certifiedDate: z.date().or(z.string()).or(z.null()).optional(),
    certificateDueDate: z.date().or(z.string()).or(z.null()).optional(),
    responsibleUserId: z.string().or(z.null()).optional(),
    responsibleUser: z
      .object({
        id: z.string(),
        email: z.string(),
        firstName: z.string().nullable().optional(),
        lastName: z.string().nullable().optional(),
        phoneNumber: z.string().nullable().optional(),
      })
      .optional()
      .nullable()
      .transform(value => (value ? value.id : undefined)),
    hasFish: z.boolean().optional(),
    hs: z
      .string()
      .or(z.number())
      .or(z.null())
      .transform(value => (typeof value === 'string' ? (value == '' ? null : Number(value)) : value))
      .refine(value => value == null || value >= 0, { message: translate('mustBeZeroOrPositive', { ns: 'common' }) })
      .optional(),
    vC5M: z
      .string()
      .or(z.number())
      .or(z.null())
      .transform(value => (typeof value === 'string' ? (value == '' ? null : Number(value)) : value))
      .refine(value => value == null || value >= 0, { message: translate('mustBeZeroOrPositive', { ns: 'common' }) })
      .optional(),
    vC15M: z
      .string()
      .or(z.number())
      .or(z.null())
      .transform(value => (typeof value === 'string' ? (value == '' ? null : Number(value)) : value))
      .refine(value => value == null || value >= 0, { message: translate('mustBeZeroOrPositive', { ns: 'common' }) })
      .optional(),
    solidityAndCircumferences: z
      .array(
        z.object({
          localityId: z.number().or(z.null()).optional(),
          solidity: z.number().or(z.string()),
          circumference: z.number().or(z.string()),
        }),
      )
      .or(z.null())
      .optional(),
    solidity: z
      .string()
      .or(z.number())
      .or(z.null())
      .transform(value => (typeof value === 'string' ? (value == '' ? null : Number(value)) : value))
      .optional(),
    circumference: z
      .string()
      .or(z.number())
      .or(z.null())
      .transform(value => (typeof value === 'string' ? (value == '' ? null : Number(value)) : value))
      .optional(),

    country: z.string().or(z.null()).optional(),
    municipality: z.string().or(z.number()).or(z.null()).optional(),
  })

  const handleChange = (e, value) => {
    form.setValue('responsibleUser', value)
    form.setValue('responsibleUserId', value?.id)
  }

  type FormSchema = z.infer<typeof schema>
  const form: UseFormReturn<FormSchema> = useZodForm(schema, {
    defaultValues: {
      ...initialValues,
      solidity: initialValues?.solidity ?? null,
      circumference: initialValues?.circumference ?? null,
    },
  })

  const { data: customerUsers } = useGetCustomerUsers(customerId as EntityId)

  useEffect(() => {
    if (initialValues?.solidityAndCircumferences) {
      initialValues?.solidityAndCircumferences.forEach(property => {
        property.uniqueId = generateUUID()
      })
      setsolidityAndCircumferences(initialValues?.solidityAndCircumferences)
    }
  }, [initialValues])

  useEffect(() => {
    form.setValue('solidityAndCircumferences', solidityAndCircumferences)
  }, [solidityAndCircumferences, form])

  const resetProperties = () => {
    properties.forEach(key => {
      form.clearErrors(key as any)
      form.setValue(key as any, null)
    })
  }

  const validateProperty = key => {
    const value = form.getValues(key)
    if (!value) {
      form.setError(key, { type: 'required', message: translate('required', { ns: 'common' }) })
      return false
    }

    const numberValue = Number(value)
    if (isNaN(numberValue) || numberValue <= 0) {
      form.setError(key, { type: 'invalid', message: translate('mustBePositive', { ns: 'common' }) })
      return false
    }

    form.clearErrors(key)
    return true
  }

  const validateProperties = () => {
    let isValid = true
    properties.forEach(key => {
      if (!validateProperty(key)) {
        isValid = false
      }
    })
    return isValid
  }

  const handleSave = () => {
    const solidity = form.getValues('solidity')
    const circumference = form.getValues('circumference')

    if (!validateProperties()) {
      return
    }
    setsolidityAndCircumferences(prev => {
      if (editingPropertyId) {
        return prev.map(p => {
          if (p.uniqueId === editingPropertyId) {
            return {
              solidity: solidity,
              circumference: circumference,
              uniqueId: editingPropertyId,
            } as ILocalitySolidityAndCircumference
          }
          return p
        })
      }
      return [
        ...prev,
        {
          solidity: solidity,
          circumference: circumference,
          uniqueId: generateUUID(),
        } as ILocalitySolidityAndCircumference,
      ]
    })
    setEditingPropertyId(null)
    resetProperties()
  }

  const handleMainButtonClick = async () => {
    const solidity = form.getValues('solidity')
    const circumference = form.getValues('circumference')

    if (solidity && circumference) {
      await new Promise(resolve => {
        handleSave()
        setTimeout(resolve, 0)
      })
      form.handleSubmit(onSubmit)()
    } else if (!solidity && !circumference) {
      form.handleSubmit(onSubmit)()
    } else {
      validateProperties()
    }
  }

  const showingProperties = useMemo(() => {
    return solidityAndCircumferences.filter(x => x.uniqueId != editingPropertyId)
  }, [editingPropertyId, solidityAndCircumferences])

  return (
    <AppForm form={form} onSubmit={handleMainButtonClick} hasInitialValues={initialValues}>
      <FormTextField
        name="name"
        label="Name"
        defaultValue={initialValues?.Name}
        inputProps={{
          style: { textTransform: 'capitalize' },
        }}
      />
      <Grid container>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12} sm={4}>
              <FormTextField name="locationNr" label={translate('locationNr', { ns: 'locality' })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormTextField name="country" label={translate('county', { ns: 'locality' })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormTextField name="municipality" label={translate('Municipality', { ns: 'locality' })} />
            </Grid>
          </Grid>
          <Box py={1} display="flex" justifyContent="flex-end">
            <Button variant="text" onClick={() => setIsExpanded(prev => !prev)}>
              {isExpanded ? translate('seeLess', { ns: 'locality' }) : translate('seeMore', { ns: 'locality' })}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {isExpanded && (
        <>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <FormTextField name="latitude" label="Gps latitude" type="number" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormTextField name="longitude" label="Gps longitude" />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <BasicDatePicker label={translate('certificateDueDate', { ns: 'locality' })} name="certificateDueDate" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <BasicDatePicker
                label={translate('certifiedDate', { ns: 'locality' })}
                name="certifiedDate"
                onChange={value => {
                  form.setValue('certifiedDate', value)
                  form.trigger('certifiedDate')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormTextField type="number" name="hs" label={translate('hs', { ns: 'common' })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormTextField type="number" name="vC5M" label={translate('vc5m', { ns: 'common' })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormTextField type="number" name="vC15M" label={translate('vc15m', { ns: 'common' })} />
            </Grid>
          </Grid>
          <Flex.Column py={1} justifyContent={'center'} alignItems={'flex-start'}>
            <Typography px={1} fontWeight={600} variant="subtitle2">
              {translate('solidityAndCircumference', { ns: 'locality' })}
            </Typography>

            <Divider />
            {showingProperties.map((property, index) => (
              <Flex.Row py={1} justifyContent={'center'} alignItems={'center'} key={index} gap={1}>
                {`#${index + 1}`}
                <Chip label={`${translate('solidity', { ns: 'locality' })} : ${property.solidity}`} />
                <Chip label={`${translate('circumference', { ns: 'locality' })} : ${property.circumference}`} />
                <Flex.Row gap={1} justifyContent={'center'} alignItems={'center'}>
                  <IconButton
                    onClick={() => {
                      const appForm = form as any
                      appForm.setValue('solidity', property.solidity)
                      appForm.setValue('circumference', property.circumference)
                      setEditingPropertyId(property.uniqueId)
                    }}
                    sx={{ width: '30px', height: '30px' }}
                    disabled={editingPropertyId !== null}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setsolidityAndCircumferences(prev => prev.filter(p => p.uniqueId !== property.uniqueId))
                    }}
                    sx={{ width: '30px', height: '30px' }}
                    disabled={editingPropertyId !== null}
                  >
                    <Delete />
                  </IconButton>
                </Flex.Row>
              </Flex.Row>
            ))}
            <Flex.Row py={1} justifyContent={'center'} alignItems={'center'}>
              <FormTextField
                type={'number'}
                name="solidity"
                label={translate('solidity', { ns: 'locality' })}
                focused={editingPropertyId && showingProperties.length == 0 ? true : undefined}
              />
              <FormTextField
                type={'number'}
                name="circumference"
                label={translate('circumference', { ns: 'locality' })}
                focused={editingPropertyId && showingProperties.length == 0 ? true : undefined}
              />
              <Flex.Row gap={1}>
                <IconButton onClick={handleSave}>
                  <Save />
                </IconButton>
                {editingPropertyId ? (
                  <IconButton
                    onClick={() => {
                      setEditingPropertyId(null)
                      resetProperties()
                    }}
                  >
                    <Close />
                  </IconButton>
                ) : (
                  <IconButton onClick={resetProperties}>
                    <RestartAlt />
                  </IconButton>
                )}
              </Flex.Row>
            </Flex.Row>
          </Flex.Column>
          <Box py={1}>
            <FormCheckbox name="notificationActive" label={translate('notificationActive', { ns: 'locality' })} />
            <FormCheckbox name="active" label={translate('active', { ns: 'locality' })} />
            {initialValues?.netCount > 0 && (
              <FormCheckbox name="hasFish" label={translate('hasFish', { ns: 'locality' })} />
            )}
          </Box>
        </>
      )}
      <Box className="flex justify-end gap-4 pt-4">
        <GridActionButton
          border={1}
          sx={{ width: '120px', height: '44px' }}
          onClick={e => {
            onStepback && onStepback(e)
            pop()
          }}
          buttonText={translate('back', { ns: 'common' })}
        />{' '}
        <FormSubmitButton
          createText={isUpdateForm ? translate('update', { ns: 'common' }) : translate('add', { ns: 'common' })}
        />
      </Box>
    </AppForm>
  )
}
